import { MdVerified, MdStar, MdLocationOn, MdChevronRight, MdMessage } from 'react-icons/md';

/**
 * Single search result card: avatar, name, badge, rating, location, price, CTA.
 * Used in list and syncs hover/selected with map.
 */
export default function ResultCard({
  item,
  isParent,
  avatarUrl,
  initials,
  isSelected,
  isHovered,
  onMouseEnter,
  onMouseLeave,
  onClick,
  onContactClick,
}) {
  const displayName = isParent
    ? `${item.firstName || ''} ${item.lastName || ''}`.trim()
    : (item.title || 'Posao');
  const city = item.address?.city || item.location?.city || 'Beograd';
  const roleLabel = isParent ? `Bebisiterka u ${city}` : (item.title || `Posao u ${city}`);
  const rating = item.rating?.average || 0;
  const ratingCount = item.rating?.count || 0;
  const experience = item.experienceYears ?? item.experience;
  const age = item.age;
  const bioSnippet = (item.bio || item.description || '').slice(0, 120);
  const isSupersitter = item.isSupersitter ?? (rating >= 4.8 && ratingCount >= 2);
  const rate = item.hourlyRate != null ? Number(item.hourlyRate) : null;

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
      className={[
        'group relative flex gap-4 p-4 rounded-2xl transition-all cursor-pointer text-left',
        'bg-white dark:bg-gray-800',
        'border border-gray-200/80 dark:border-gray-700/80',
        'shadow-sm hover:shadow-md',
        isHovered ? 'ring-2 ring-primary-200/70 dark:ring-primary-900/40' : '',
        isSelected ? 'ring-2 ring-primary-300 dark:ring-primary-800 border-primary-200 dark:border-primary-700' : '',
      ].filter(Boolean).join(' ')}
      aria-pressed={isSelected}
      aria-label={`${displayName}, ${roleLabel}`}
    >
      {/* Accent */}
      <div
        className={[
          'absolute inset-y-3 left-3 w-1 rounded-full',
          isSelected ? 'bg-primary-500' : 'bg-primary-200 group-hover:bg-primary-300 dark:bg-primary-900/40 dark:group-hover:bg-primary-800/60',
        ].join(' ')}
        aria-hidden
      />

      <div className="flex-shrink-0 relative ml-2">
        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gray-100 dark:bg-gray-700 overflow-hidden border border-gray-200 dark:border-gray-600 flex items-center justify-center text-primary-600 dark:text-primary-400 font-semibold text-lg shadow-sm">
          {avatarUrl ? (
            <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            <span>{initials || '?'}</span>
          )}
        </div>
        {isParent && isSupersitter && (
          <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-rose-500 text-white shadow-sm whitespace-nowrap">
            Super
          </span>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-gray-900 dark:text-gray-100 text-[15px] sm:text-base leading-tight break-words min-w-0">
                {displayName}
              </h3>
              {item.verified && (
                <MdVerified className="text-primary-600 dark:text-primary-400 flex-shrink-0 text-lg" title="Proveren profil" aria-hidden />
              )}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 truncate">
              {roleLabel}
            </p>
          </div>

          {rate != null && (
            <div className="flex-shrink-0 text-right">
              <div className="inline-flex items-center px-2.5 py-1 rounded-full bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border border-primary-200/70 dark:border-primary-800/50 text-xs font-semibold tabular-nums">
                {rate.toLocaleString()} RSD/h
              </div>
            </div>
          )}
        </div>

        {bioSnippet && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1.5 line-clamp-2">
            {bioSnippet}{(item.bio || item.description || '').length > 120 ? '…' : ''}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-gray-500 dark:text-gray-400">
          {rating > 0 && (
            <span className="flex items-center gap-0.5 font-medium">
              <MdStar className="text-amber-500 fill-amber-500" aria-hidden /> {rating.toFixed(1)}
              {ratingCount > 0 && <span className="text-gray-400">({ratingCount})</span>}
            </span>
          )}
          {(experience != null || age != null) && (
            <span>
              {experience != null && `${experience}+ god.`}
              {experience != null && age != null && ' · '}
              {age != null && `${age} god.`}
            </span>
          )}
          <span className="flex items-center gap-0.5">
            <MdLocationOn className="text-gray-400" aria-hidden /> {city}
          </span>
        </div>
        <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
          {onContactClick && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onContactClick(item);
              }}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/40 border border-primary-200/70 dark:border-primary-800/50"
            >
              <MdMessage className="text-sm" aria-hidden />
              Kontaktiraj
            </button>
          )}
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary-700 dark:text-primary-300 group-hover:text-primary-800 dark:group-hover:text-primary-200">
            Profil <MdChevronRight className="text-base" />
          </span>
        </div>
      </div>
    </article>
  );
}
